import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const supplySectionDiagram1 = '/images/aip/Supplychain/business-collaboration-network.png';
const supplySectionDiagram2 = '/images/aip/Supplychain/intelligent-control-tower.png';
const supplySectionDiagram3 = '/images/aip/Supplychain/embedded-business-engine.png';
const supplySectionDiagram4 = '/images/aip/Supplychain/deep-operational-insight.png';
const case01 = '/images/aip/Retail/case-01.png';
const bannerBg = '/images/aip/Supplychain/supplychain-banner-bg.svg';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '业务协同网络',
    subtitle: '打破组织边界，让信息实时流动',
    previewAlt: '业务协同网络示意图',
    previewImage: supplySectionDiagram1,
    items: [
      {
        title: '全链路流程在线',
        description:
          '实现从采购订单、交期确认到销售订单、发货状态的全流程线上协同与状态透明，打破信息孤岛。'
      },
      {
        title: '多渠道库存可视',
        description:
          '提供跨仓库、渠道、门店及在途库存的统一实时视图，任何移动即时同步，实现全局一本账。'
      },
      {
        title: '生态伙伴门户',
        description: '为客户与供应商提供专属门户，共享订单、库存、物流数据，提升协同效率与体验。'
      },
      {
        title: '物流状态跟踪',
        description: '集成承运商系统，实现从出库、运输到签收的全节点实时追踪，显著提升交付确定性。'
      }
    ]
  },
  {
    title: '智能控制塔',
    subtitle: '从数据到决策，前瞻视角指挥全局',
    previewAlt: '智能控制塔示意图',
    previewImage: supplySectionDiagram2,
    items: [
      {
        title: '全局可视化指挥',
        description:
          '一屏汇聚采购、销售、库存核心指标与健康状态，快速洞察全局，辅助管理者快速拍板。'
      },
      {
        title: '智能预警与通知',
        description:
          '实时监控交期延迟、库存超限、需求突变等风险，并主动推送预警，变被动应对为主动干预。'
      },
      {
        title: '"What-if"情景推演',
        description:
          '通过模拟分析，评估供应商切换、促销活动等决策对成本、库存与交付的影响，在执行前预知结果。'
      },
      {
        title: 'AI驱动科学补货',
        description:
          '告别经验主义，基于算法模型动态计算安全库存与补货策略，在保障供应的同时优化库存成本。'
      }
    ]
  },
  {
    title: '嵌入式业务引擎',
    subtitle: '将管理意志嵌到每一行代码',
    previewAlt: '嵌入式业务引擎示意图',
    previewImage: supplySectionDiagram3,
    items: [
      {
        title: '流程自动化执行',
        description:
          '从采购申请、销售订单到发货过账，业务逻辑自动触发，减少90%人工干预，大幅提升准确性与流转速度。'
      },
      {
        title: '硬核合规约束',
        description:
          '价格超标、信用不足、配额溢出？系统瞬间拦截。在业务发生瞬间自动完成合规校验，守住利润底线。'
      },
      {
        title: '内外协同闭环',
        description:
          '内外部信息自动对齐。采购发货通知、客户收货确认自动关联，确保数据流、物流、财务流三流合一。'
      },
      {
        title: '动态策略调度',
        description:
          '智能分仓、分级定价、优先级调度，系统根据实时库存水位与客户等级自动执行最优方案。'
      }
    ]
  },
  {
    title: '深度运营洞察',
    subtitle: '挖掘增长空间，持续优化盈利能力',
    previewAlt: '深度运营洞察示意图',
    previewImage: supplySectionDiagram4,
    items: [
      {
        title: '流程挖掘与还原',
        description:
          '自动还原订单到现金、采购到付款的真实流程路径，可视化呈现断点、延误与合规风险。'
      },
      {
        title: '多维度效能诊断',
        description:
          '定量分析订单履行周期、库存周转率、供应商准时率等核心指标，精准定位瓶颈环节与改进空间。'
      },
      {
        title: '支出与成本优化',
        description:
          '全面洞察采购支出结构、库存健康度与客户贡献，识别呆滞物料风险，挖掘省钱机会与价值客户。'
      },
      {
        title: '预测性计划支持',
        description:
          '结合历史数据与市场洞察，辅助需求预测与采购计划制定，推动供应链从被动执行向主动规划转变。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Supplychain() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;

    const note =
      '帮助企业打破内外部信息孤岛，通过“连接+数据”双轮驱动，将传统的“被动执行”转变为“主动掌控”，实现供应韧性与运营效率的双重跃升。';

    const noteTips: NoteTip[] = [
      { value: '+35%', label: '订单交付准时率' },
      { value: '100%', label: '全局库存可视度' },
      { value: '+60%', label: '异常响应速度' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '业务应用-供应链',
      subheading: '',
      legend: '打造可视、可控、可预测的企业供应链新引擎',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, []);

  return (
    <div className="supplychain-page">
      <div className="aip-banner-wrap supplychain-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">智慧零售运营中枢实践案例</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">智慧零售全渠道业务增长实践</h3>
                    <p className="case-desc">
                      通过一盘货、统一订单中心和客户引擎协同联动，帮助企业提升库存周转效率、履约人效与会员复购表现。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="零售案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
